/**
 * Forecasting Dashboard
 * 
 * Cost report / forecasting dashboard for project financial tracking.
 * Mimics the piping tracker style with all standard buttons.
 * 
 * Created: December 19, 2025
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useMsal } from '@azure/msal-react';
import { authenticatedApiCall } from '../../utils/apiClient';
import { usePresence, ActiveViewers } from '../common/ViewingTemplate';
import useColumnOrder from '../../hooks/useColumnOrder';
import ColumnConfigPanel from '../common/ColumnConfigPanel';
import { applyColumnOrder } from '../../utils/columnLayout';

// Column configuration matching the spreadsheet structure
const COLUMN_GROUPS = [
  {
    id: 'identification',
    label: 'Identification',
    color: '#f0f0f0',
    columns: [
      { key: 'activ_code', label: 'Activ Code', width: 100, editable: true },
      { key: 'activ_group', label: 'Group', width: 60, editable: true },
      { key: 'activ_type', label: 'Type', width: 80, editable: true },
      { key: 'activ_desc', label: 'Description', width: 200, editable: true }
    ]
  },
  {
    id: 'budget',
    label: 'Budget',
    color: '#fff2cc',
    columns: [
      { key: 'orig_budget', label: 'Orig. Budget', width: 100, type: 'currency', editable: true },
      { key: 'curr_budget', label: 'Curr. Budget', width: 100, type: 'currency', editable: true },
      { key: 'curr_commit', label: 'Curr. Commit', width: 100, type: 'currency', editable: true },
      { key: 'remain_to_commit', label: 'Remain. to Commit', width: 120, type: 'currency', editable: false }
    ]
  },
  {
    id: 'actual_spent',
    label: 'Actual Spent to Date, with Accruals',
    color: '#d9ead3',
    columns: [
      { key: 'posted_spent_to_date', label: 'Posted Spent', width: 100, type: 'currency', editable: true },
      { key: 'previous_estd_accruals', label: 'Prev Est\'d Accruals', width: 120, type: 'currency', editable: true },
      { key: 'estd_accruals', label: 'Est\'d Accruals', width: 100, type: 'currency', editable: true },
      { key: 'posted_plus_accruals', label: 'Posted+Accruals', width: 110, type: 'currency', editable: false },
      { key: 'pct_spent_posted', label: '% Spent Posted', width: 100, type: 'percent', editable: false },
      { key: 'pct_spent_estd', label: '% Spent Est\'d', width: 100, type: 'percent', editable: false },
      { key: 'pct_compl', label: '% Compl', width: 80, type: 'percent', editable: true },
      { key: 'physical_value', label: 'Physical Value', width: 100, type: 'currency', editable: false },
      { key: 'budg_earned', label: 'Budg-Earned', width: 100, type: 'currency', editable: false },
      { key: 'variance_remain_to_spend', label: 'Variance', width: 100, type: 'currency', editable: false }
    ]
  },
  {
    id: 'forecasted_costs',
    label: 'Forecasted Costs',
    color: '#cfe2f3',
    columns: [
      { key: 'previous_etc_estim', label: 'Prev ETC Estim', width: 100, type: 'currency', editable: true },
      { key: 'etc_estim_to_compl', label: 'ETC Estim', width: 100, type: 'currency', editable: false },
      { key: 'etc_to_use', label: 'ETC To Use', width: 100, type: 'currency', editable: true },
      { key: 'previous_fac', label: 'Prev FAC', width: 100, type: 'currency', editable: true },
      { key: 'fac_forecast_at_compl', label: 'FAC', width: 100, type: 'currency', editable: false },
      { key: 'forecasted_variance_dollars', label: 'Fore Var $', width: 100, type: 'currency', editable: false },
      { key: 'forecasted_variance_pct', label: 'Fore Var %', width: 80, type: 'percent', editable: false },
      { key: 'forecasted_remain_to_commit', label: 'Fore Remain', width: 100, type: 'currency', editable: false }
    ]
  },
  {
    id: 'revenues_pl',
    label: 'Forecasted Revenues & P/L',
    color: '#f4cccc',
    columns: [
      { key: 'estd_markup_orig', label: 'Est\'d M/U Orig', width: 100, type: 'currency', editable: true },
      { key: 'estd_markup_curr', label: 'Est\'d M/U Curr', width: 100, type: 'currency', editable: true },
      { key: 'orig_estd_revenue', label: 'Orig Revenue', width: 100, type: 'currency', editable: false },
      { key: 'curr_estd_revenue', label: 'Curr Revenue', width: 100, type: 'currency', editable: false },
      { key: 'previous_fored_profit', label: 'Prev Profit', width: 100, type: 'currency', editable: true },
      { key: 'fored_profit_dollars', label: 'Fore Profit $', width: 100, type: 'currency', editable: false },
      { key: 'fored_profit_pct', label: 'Fore Profit %', width: 80, type: 'percent', editable: false },
      { key: 'estd_rev_fac', label: 'Est\'d Rev FAC', width: 100, type: 'currency', editable: false }
    ]
  },
  {
    id: 'cash_flow',
    label: 'Cash Flow',
    color: '#d9d2e9',
    columns: [
      { key: 'actual_revenue_to_date', label: 'Actual Revenue', width: 110, type: 'currency', editable: true },
      { key: 'remain_to_receive', label: 'Remain to Receive', width: 120, type: 'currency', editable: false },
      { key: 'present_cash_flow', label: 'Present Cash Flow', width: 120, type: 'currency', editable: false }
    ]
  }
];

// Flatten columns for easy access
const ALL_COLUMNS = COLUMN_GROUPS.flatMap(group => 
  group.columns.map(col => ({ ...col, groupId: group.id, groupLabel: group.label, groupColor: group.color }))
);

const ForecastingDashboard = ({ jobNumber, projectDetails }) => {
  const { instance, accounts } = useMsal();

  // Live data push support
  const loadDataRef = useRef(null);
  const handleDataChange = useCallback(() => {
    if (loadDataRef.current) loadDataRef.current();
  }, []);

  // Real-time presence tracking (shows who's viewing this page)
  const { viewers, isConnected, connectionState, socketId } = usePresence('forecastingOverview', jobNumber || 'global', true, handleDataChange);

  const socketIdRef = useRef(null);
  useEffect(() => { socketIdRef.current = socketId; }, [socketId]);

  const syncHeaders = useCallback(() => {
    return socketIdRef.current ? { headers: { 'x-socket-id': socketIdRef.current } } : {};
  }, []);

  // State
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingCell, setEditingCell] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [showColumnConfig, setShowColumnConfig] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState(
    ALL_COLUMNS.reduce((acc, col) => ({ ...acc, [col.key]: true }), {})
  );
  const { columnOrder, moveColumn } = useColumnOrder('forecasting-legacy-column-order', ALL_COLUMNS);
  
  // Load data
  const loadData = useCallback(async () => {
    if (!jobNumber) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await authenticatedApiCall(
        instance, accounts, 'GET',
        `/forecasting?jobNumber=${encodeURIComponent(jobNumber)}`
      );
      
      if (response.data.success) {
        setData(response.data.data || []);
      } else {
        setData([]);
      }
    } catch (err) {
      console.error('Error loading forecasting data:', err);
      // If API doesn't exist yet, just show empty data
      if (err.response?.status === 404) {
        setData([]);
      } else {
        setError('Failed to load data');
      }
    } finally {
      setLoading(false);
    }
  }, [jobNumber, instance, accounts]);

  useEffect(() => { loadDataRef.current = loadData; }, [loadData]);
  
  useEffect(() => {
    loadData();
  }, [loadData]);
  
  // Format value for display
  const formatValue = (value, type) => {
    if (value === null || value === undefined || value === '') return '-';
    
    switch (type) {
      case 'currency':
        const num = parseFloat(value);
        if (isNaN(num)) return value;
        return num.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 });
      case 'percent':
        const pct = parseFloat(value);
        if (isNaN(pct)) return value;
        return `${pct.toFixed(1)}%`;
      default:
        return value;
    }
  };
  
  // Handle cell edit
  const handleCellClick = (rowId, columnKey, currentValue) => {
    const column = ALL_COLUMNS.find(c => c.key === columnKey);
    if (!column?.editable) return;
    
    setEditingCell({ rowId, columnKey });
    setEditValue(currentValue || '');
  };
  
  const handleCellSave = async () => {
    if (!editingCell) return;
    
    const { rowId, columnKey } = editingCell;
    
    try {
      // Update local state immediately
      setData(prev => prev.map(row => 
        row.id === rowId ? { ...row, [columnKey]: editValue } : row
      ));
      
      // Save to server
      await authenticatedApiCall(
        instance, accounts, 'PUT',
        `/forecasting/${rowId}`,
        { [columnKey]: editValue },
        syncHeaders()
      );
    } catch (err) {
      console.error('Error saving cell:', err);
      // Reload data on error
      loadData();
    }
    
    setEditingCell(null);
    setEditValue('');
  };
  
  // Handle row selection
  const handleRowSelect = (rowId) => {
    setSelectedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(rowId)) {
        newSet.delete(rowId);
      } else {
        newSet.add(rowId);
      }
      return newSet;
    });
  };
  
  const handleSelectAll = () => {
    if (selectedRows.size === filteredData.length) {
      setSelectedRows(new Set());
    } else {
      setSelectedRows(new Set(filteredData.map(row => row.id)));
    }
  };
  
  // Filter and sort data
  const filteredData = useMemo(() => {
    let result = [...data];
    
    // Search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(row => 
        Object.values(row).some(val => 
          val && String(val).toLowerCase().includes(term)
        )
      );
    }
    
    // Sort
    if (sortConfig.key) {
      result.sort((a, b) => {
        const aVal = a[sortConfig.key] || '';
        const bVal = b[sortConfig.key] || '';
        const comparison = String(aVal).localeCompare(String(bVal), undefined, { numeric: true });
        return sortConfig.direction === 'asc' ? comparison : -comparison;
      });
    }
    
    return result;
  }, [data, searchTerm, sortConfig]);
  
  // Handle sort
  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };
  
  // Add new row
  const handleAddRow = async () => {
    try {
      const response = await authenticatedApiCall(
        instance, accounts, 'POST',
        '/forecasting',
        { job_number: jobNumber },
        syncHeaders()
      );
      
      if (response.data.success) {
        loadData();
      }
    } catch (err) {
      console.error('Error adding row:', err);
    }
  };
  
  // Delete selected rows
  const handleDeleteSelected = async () => {
    if (selectedRows.size === 0) return;
    
    if (!window.confirm(`Delete ${selectedRows.size} row(s)?`)) return;
    
    try {
      await Promise.all(
        Array.from(selectedRows).map(id =>
          authenticatedApiCall(instance, accounts, 'DELETE', `/forecasting/${id}`, null, syncHeaders())
        )
      );
      
      setSelectedRows(new Set());
      loadData();
    } catch (err) {
      console.error('Error deleting rows:', err);
    }
  };
  
  // Export to Excel
  const handleExport = () => {
    // Create CSV content
    const headers = ALL_COLUMNS.filter(c => visibleColumns[c.key]).map(c => c.label);
    const csvContent = [
      headers.join(','),
      ...filteredData.map(row => 
        ALL_COLUMNS
          .filter(c => visibleColumns[c.key])
          .map(c => {
            const val = row[c.key];
            return val !== null && val !== undefined ? `"${String(val).replace(/"/g, '""')}"` : '';
          })
          .join(',')
      )
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `forecasting-${jobNumber}-${Date.now()}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };
  
  // Calculate totals
  const totals = useMemo(() => {
    const result = {};
    ALL_COLUMNS.forEach(col => {
      if (col.type === 'currency') {
        result[col.key] = filteredData.reduce((sum, row) => 
          sum + (parseFloat(row[col.key]) || 0), 0
        );
      }
    });
    return result;
  }, [filteredData]);
  
  // Visible columns for rendering
  const visibleColumnsList = applyColumnOrder(ALL_COLUMNS, columnOrder).filter(c => visibleColumns[c.key]);

  // Compute frozen (identification) vs scrollable columns with left offsets
  const frozenColumns = useMemo(() => {
    const cols = visibleColumnsList.filter(c => c.groupId === 'identification');
    let left = 40; // starts after checkbox column
    return cols.map(col => {
      const item = { ...col, leftOffset: left };
      left += col.width;
      return item;
    });
  }, [visibleColumnsList]);

  const scrollableColumns = useMemo(() => {
    return visibleColumnsList.filter(c => c.groupId !== 'identification');
  }, [visibleColumnsList]);
  
  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loading}>Loading forecasting data...</div>
      </div>
    );
  }
  
  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.titleSection}>
          <h2 style={styles.title}>Forecasting Dashboard</h2>
          <span style={styles.subtitle}>
            {projectDetails?.project_name || jobNumber} • {filteredData.length} rows
          </span>
        </div>
        
        {/* Presence indicator */}
        <ActiveViewers viewers={viewers} isConnected={isConnected} connectionState={connectionState} maxVisible={4} showConnectionStatus={true} />

        {/* Toolbar */}
        <div style={styles.toolbar}>
          <input
            type="text"
            placeholder="Search..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={styles.searchInput}
          />
          
          <button style={styles.button} onClick={handleAddRow}>
            + Add Row
          </button>
          
          <button 
            style={{...styles.button, ...styles.dangerButton}}
            onClick={handleDeleteSelected}
            disabled={selectedRows.size === 0}
          >
            🗑️ Delete ({selectedRows.size})
          </button>
          
          <button style={styles.button} onClick={handleExport}>
            📥 Export
          </button>
          
          <button style={styles.button} onClick={loadData}>
            🔄 Refresh
          </button>
          
          <button 
            style={styles.button}
            onClick={() => setShowColumnConfig(!showColumnConfig)}
          >
            ⚙️ Columns
          </button>
        </div>
      </div>
      
      {/* Column config panel */}
      {showColumnConfig && (
        <div style={styles.columnConfigPanel}>
          <ColumnConfigPanel
            title="Show/Hide Columns"
            columns={applyColumnOrder(ALL_COLUMNS, columnOrder)}
            isVisible={(key) => !!visibleColumns[key]}
            onToggle={(key) => setVisibleColumns(prev => ({ ...prev, [key]: !prev[key] }))}
            onMove={moveColumn}
            onShowAll={() => setVisibleColumns(ALL_COLUMNS.reduce((acc, c) => ({ ...acc, [c.key]: true }), {}))}
            compact={false}
          />
        </div>
      )}
      
      {error && <div style={styles.error}>{error}</div>}
      
      {/* Table - two-panel layout: frozen left + scrollable right */}
      <div style={styles.tableContainer}>
        {/* Frozen left panel (Identification columns) */}
        <div style={styles.frozenPanel}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={{ ...styles.thGroup, backgroundColor: '#f0f0f0' }} colSpan={frozenColumns.length + 1}>
                  Identification
                </th>
              </tr>
              <tr>
                <th style={{ ...styles.th, width: 40, backgroundColor: '#fff' }}>
                  <input
                    type="checkbox"
                    checked={selectedRows.size === filteredData.length && filteredData.length > 0}
                    onChange={handleSelectAll}
                  />
                </th>
                {frozenColumns.map(col => (
                  <th 
                    key={col.key}
                    style={{ ...styles.th, width: col.width, minWidth: col.width, backgroundColor: col.groupColor, cursor: 'pointer' }}
                    onClick={() => handleSort(col.key)}
                  >
                    {col.label}
                    {sortConfig.key === col.key && (
                      <span style={styles.sortIndicator}>
                        {sortConfig.direction === 'asc' ? ' ↑' : ' ↓'}
                      </span>
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredData.length === 0 ? (
                <tr>
                  <td colSpan={frozenColumns.length + 1} style={styles.emptyRow}>
                    No data.
                  </td>
                </tr>
              ) : (
                filteredData.map(row => (
                  <tr key={row.id} style={selectedRows.has(row.id) ? styles.selectedRow : {}}>
                    <td style={{ ...styles.td, backgroundColor: selectedRows.has(row.id) ? '#e3f2fd' : '#fff' }}>
                      <input
                        type="checkbox"
                        checked={selectedRows.has(row.id)}
                        onChange={() => handleRowSelect(row.id)}
                      />
                    </td>
                    {frozenColumns.map(col => (
                      <td 
                        key={col.key}
                        style={{ 
                          ...styles.td,
                          ...(col.editable ? styles.editableCell : {}),
                          backgroundColor: selectedRows.has(row.id) ? '#e3f2fd' : '#f8f8f8'
                        }}
                        onClick={() => handleCellClick(row.id, col.key, row[col.key])}
                      >
                        {editingCell?.rowId === row.id && editingCell?.columnKey === col.key ? (
                          <input
                            type={col.type === 'currency' || col.type === 'percent' ? 'number' : 'text'}
                            value={editValue}
                            onChange={(e) => setEditValue(e.target.value)}
                            onBlur={handleCellSave}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') handleCellSave();
                              if (e.key === 'Escape') setEditingCell(null);
                            }}
                            style={styles.cellInput}
                            autoFocus
                          />
                        ) : (
                          formatValue(row[col.key], col.type)
                        )}
                      </td>
                    ))}
                  </tr>
                ))
              )}
              {filteredData.length > 0 && (
                <tr style={styles.totalsRow}>
                  <td style={styles.td}></td>
                  {frozenColumns.map(col => (
                    <td key={col.key} style={{ ...styles.td, fontWeight: 'bold' }}>
                      {col.type === 'currency' ? formatValue(totals[col.key], 'currency') : ''}
                    </td>
                  ))}
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Scrollable right panel */}
        <div style={styles.scrollablePanel}>
          <table style={styles.table}>
            <thead>
              <tr>
                {COLUMN_GROUPS.filter(g => g.id !== 'identification').map(group => {
                  const visibleInGroup = group.columns.filter(c => visibleColumns[c.key]);
                  if (visibleInGroup.length === 0) return null;
                  return (
                    <th 
                      key={group.id}
                      colSpan={visibleInGroup.length}
                      style={{ ...styles.thGroup, backgroundColor: group.color }}
                    >
                      {group.label}
                    </th>
                  );
                })}
              </tr>
              <tr>
                {scrollableColumns.map(col => (
                  <th 
                    key={col.key}
                    style={{ ...styles.th, width: col.width, minWidth: col.width, backgroundColor: col.groupColor, cursor: 'pointer' }}
                    onClick={() => handleSort(col.key)}
                  >
                    {col.label}
                    {sortConfig.key === col.key && (
                      <span style={styles.sortIndicator}>
                        {sortConfig.direction === 'asc' ? ' ↑' : ' ↓'}
                      </span>
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredData.length === 0 ? (
                <tr>
                  <td colSpan={scrollableColumns.length} style={styles.emptyRow}>
                    Click "Add Row" to create a new entry.
                  </td>
                </tr>
              ) : (
                filteredData.map(row => (
                  <tr key={row.id} style={selectedRows.has(row.id) ? styles.selectedRow : {}}>
                    {scrollableColumns.map(col => (
                      <td 
                        key={col.key}
                        style={{ 
                          ...styles.td,
                          ...(col.editable ? styles.editableCell : {}),
                          backgroundColor: selectedRows.has(row.id) ? '#e3f2fd' : (col.groupColor + '40')
                        }}
                        onClick={() => handleCellClick(row.id, col.key, row[col.key])}
                      >
                        {editingCell?.rowId === row.id && editingCell?.columnKey === col.key ? (
                          <input
                            type={col.type === 'currency' || col.type === 'percent' ? 'number' : 'text'}
                            value={editValue}
                            onChange={(e) => setEditValue(e.target.value)}
                            onBlur={handleCellSave}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') handleCellSave();
                              if (e.key === 'Escape') setEditingCell(null);
                            }}
                            style={styles.cellInput}
                            autoFocus
                          />
                        ) : (
                          formatValue(row[col.key], col.type)
                        )}
                      </td>
                    ))}
                  </tr>
                ))
              )}
              {filteredData.length > 0 && (
                <tr style={styles.totalsRow}>
                  {scrollableColumns.map(col => (
                    <td key={col.key} style={{ ...styles.td, fontWeight: 'bold' }}>
                      {col.type === 'currency' ? formatValue(totals[col.key], 'currency') : ''}
                    </td>
                  ))}
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

const styles = {
  container: {
    padding: '20px',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '20px',
    flexWrap: 'wrap',
    gap: '16px'
  },
  titleSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px'
  },
  title: {
    margin: 0,
    fontSize: '24px',
    fontWeight: '600',
    color: '#1a1a2e'
  },
  subtitle: {
    color: '#666',
    fontSize: '14px'
  },
  toolbar: {
    display: 'flex',
    gap: '8px',
    flexWrap: 'wrap',
    alignItems: 'center'
  },
  searchInput: {
    padding: '8px 12px',
    border: '1px solid #ddd',
    borderRadius: '6px',
    fontSize: '14px',
    width: '200px'
  },
  button: {
    padding: '8px 16px',
    backgroundColor: '#007bff',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '14px',
    display: 'flex',
    alignItems: 'center',
    gap: '4px'
  },
  dangerButton: {
    backgroundColor: '#dc3545'
  },
  linkButton: {
    background: 'none',
    border: 'none',
    color: '#007bff',
    cursor: 'pointer',
    fontSize: '13px'
  },
  columnConfigPanel: {
    backgroundColor: '#f8f9fa',
    border: '1px solid #ddd',
    borderRadius: '8px',
    padding: '16px',
    marginBottom: '16px'
  },
  columnConfigHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '12px'
  },
  columnConfigGrid: {
    display: 'flex',
    gap: '16px',
    flexWrap: 'wrap'
  },
  columnConfigGroup: {
    minWidth: '150px'
  },
  columnConfigGroupLabel: {
    padding: '4px 8px',
    borderRadius: '4px',
    fontSize: '12px',
    fontWeight: 'bold',
    marginBottom: '8px'
  },
  columnConfigItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '13px',
    marginBottom: '4px',
    cursor: 'pointer'
  },
  loading: {
    textAlign: 'center',
    padding: '60px',
    color: '#666'
  },
  error: {
    backgroundColor: '#f8d7da',
    color: '#721c24',
    padding: '12px',
    borderRadius: '6px',
    marginBottom: '16px'
  },
  tableContainer: {
    display: 'flex',
    border: '1px solid #ddd',
    borderRadius: '8px',
    overflow: 'hidden'
  },
  frozenPanel: {
    flexShrink: 0,
    overflowX: 'hidden',
    borderRight: '2px solid #bbb',
    boxShadow: '2px 0 4px rgba(0,0,0,0.1)',
    zIndex: 2,
    backgroundColor: '#fff'
  },
  scrollablePanel: {
    overflowX: 'auto',
    flexGrow: 1
  },
  table: {
    width: '100%',
    borderCollapse: 'separate',
    borderSpacing: 0,
    fontSize: '13px'
  },
  thGroup: {
    padding: '8px 12px',
    textAlign: 'center',
    fontWeight: 'bold',
    borderBottom: '2px solid #999',
    fontSize: '12px',
    height: '36px',
    boxSizing: 'border-box'
  },
  th: {
    padding: '8px 12px',
    textAlign: 'left',
    fontWeight: '600',
    borderBottom: '1px solid #ddd',
    whiteSpace: 'nowrap',
    fontSize: '11px',
    height: '34px',
    boxSizing: 'border-box'
  },
  td: {
    padding: '6px 10px',
    borderBottom: '1px solid #eee',
    whiteSpace: 'nowrap',
    height: '32px',
    boxSizing: 'border-box'
  },
  editableCell: {
    cursor: 'pointer'
  },
  selectedRow: {
    backgroundColor: '#e3f2fd'
  },
  emptyRow: {
    padding: '40px',
    textAlign: 'center',
    color: '#666'
  },
  totalsRow: {
    backgroundColor: '#f0f0f0'
  },
  sortIndicator: {
    fontSize: '10px'
  },
  cellInput: {
    width: '100%',
    padding: '4px',
    border: '1px solid #007bff',
    borderRadius: '4px',
    fontSize: '13px'
  }
};

export default ForecastingDashboard;
